import type { ComponentType, SVGProps } from 'react'
import {
  Activity,
  Award,
  Boxes,
  ArrowUp,
  AtSign,
  BarChart3,
  BellRing,
  BookOpen,
  Building2,
  Bell,
  Calendar,
  CalendarClock,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  Combine,
  Cpu,
  CreditCard,
  Download,
  Gauge,
  Facebook,
  FileClock,
  FileUp,
  Gift,
  Handshake,
  Hash,
  Heart,
  HelpCircle,
  Home,
  Inbox,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  LayoutTemplate,
  Lightbulb,
  Link2,
  List,
  ListChecks,
  FileText,
  Mail,
  Paperclip,
  MapPin,
  Megaphone,
  MessageSquare,
  MessageSquareQuote,
  Palette,
  Pencil,
  Phone,
  PieChart,
  Printer,
  QrCode,
  Receipt,
  Newspaper,
  Search,
  Settings,
  Settings2,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Star,
  Store,
  Sun,
  UtensilsCrossed,
  Wallet,
  WalletCards,
  TableProperties,
  Tag,
  Target,
  Ticket,
  UserCheck,
  Users,
  Utensils,
  Wifi,
  Zap,
} from 'lucide-react'
import { SquareNavIcon } from '@/ux/icons/square'
import { CraneIcon } from '@/ux/icons/crane'
import { NewDotIcon } from 'tailwind-v4/blocks/feature-announcement'
// Types + the pure policy only, never the barrel: `@/access` also exports the
// provider and the gate components, and pulling React Router / tailwind-v4 into
// this data module is how import cycles start.
import type { AccessSubject, Capability } from '@/access/access.types'
import { can } from '@/access/access.policy'
import { isPaidVenue } from '@/utils/venue-access'
import type { HardwareTier } from '@/hooks/use-hardware-tier'
import type { VendorType } from '@/state/wifi/types'
import { WIFI_HARDWARE_GROUPS } from './wifi-hardware-groups'

export type NavIcon = ComponentType<SVGProps<SVGSVGElement>>

export interface NavContext {
  orgId: string
  /**
   * The venue currently in the URL, derived from `/:orgId/venues/:serial/...`.
   * Venue-scoped routes all live under `/venues/:serial/<section>`, so this
   * is the single source of truth — no sibling "scoped serial" field and no
   * per-section strategy. Undefined when no venue is in the URL.
   */
  serial?: string
  /**
   * The loyalty scheme currently in the URL, derived from
   * `/:orgId/loyalty/:schemeId/...` or `/:orgId/venues/:serial/loyalty/:schemeId/...`.
   * Scheme-scoped loyalty services (rewards, vouchers, customers…) use this.
   * Undefined when no scheme is in the URL.
   */
  schemeId?: string
  venue?: { type?: unknown; string_type?: unknown } | null
  /**
   * Logged-in user's email. Used for staff-only `isVisible` gates
   * (e.g. Engineering Agents). Undefined while the user query is
   * still loading.
   */
  userEmail?: string
  /**
   * Backend data-provider ids the org currently supports (e.g.
   * `morpheus`, `morpheus_simple_segments`), from
   * `useSupportedDataProviders`. Used to gate marketing nav items on
   * provider availability. Defaults to `[]` while the query loads, so
   * gated items are briefly hidden — the same fail-closed pattern as
   * `isPaidVenue` and the `access` subject.
   */
  dataProviders?: string[]
  /**
   * Metadata for supported data providers, including labels and descriptions.
   * Defaults to `{}` while the query loads. Used to resolve data provider
   * display names dynamically in nav items.
   */
  dataProviderMetadata?: Record<string, { label: string; description: string }>
  /**
   * The venue's Wi-Fi hardware tier (`snapguard` / `unifi` / `mikrotik` /
   * `meraki` / `click-through` / `unconfigured`), from `useHardwareTier`.
   * Drives which
   * Wi-Fi hardware group and children a venue sees. Undefined off venue
   * scope or while the vendor query loads — gates fall back to hidden, same
   * pattern as `isPaidVenue`.
   */
  /**
   * Whether the org has at least one ACTIVE Square integration.
   *
   * Gates Full Bhuna: the report's revenue, item and basket figures all come
   * from Square, so without an integration most of it reads as "no data" and
   * the composite score is computed from a rump of the services. Better not
   * to offer it at all than to offer a hollow version.
   *
   * Defaults to `false` while the query loads, so the gate fails CLOSED and
   * the item appears once confirmed — same pattern as `dataProviders` and
   * `isPaidVenue`.
   */
  hasSquare?: boolean
  /**
   * Whether Mouse says this org has ordering switched on, from the ONE gate
   * hook `useOrderingCapabilities`. Both nav ctx builders supply it; the
   * Ordering section is hidden until it is confirmed `true` (fails closed,
   * like `hasSquare`).
   */
  hasOrdering?: boolean
  hardwareTier?: HardwareTier
  /**
   * The venue's raw Wi-Fi vendor key (`unifi` / `mikrotik` / `meraki` / …),
   * from `useHardwareTier`. For the rare gate that needs to distinguish a
   * specific click-through vendor — prefer `hardwareTier` for everything
   * else.
   */
  vendorKey?: VendorType
  /**
   * Count of the current user's own pending invitations to join
   * organisations (not the org's sent invites). Drives the "Invitations"
   * nav item's visibility and its count badge. Undefined/0 while the query
   * loads or when there are none — gate fails CLOSED, same pattern as
   * `hasSquare`.
   */
  pendingInvitationsCount?: number
  /**
   * Does the sidebar carry the "Getting set up" row, from
   * `useOnboardingSetup`. Decided by morpheus's delivery model
   * (`portalVisibility`) and passed straight through: the schema is
   * explicit that `onboarding_live` false hides the portal whatever
   * else is true, and a nav that re-derived this would out-vote the
   * switch somebody flips when a customer asks to stop seeing it.
   *
   * Deliberately NOT the outstanding count: a job that completed this
   * morning has nothing polled against it yet, so the count is zero
   * while the list is very much unfinished.
   *
   * False while the query loads and for the great majority of
   * organisations, which never went through onboarding at all — the
   * gate fails CLOSED, same pattern as `pendingInvitationsCount`.
   */
  onboardingNav?: boolean
  /** Outstanding checks, for the row's badge only. Never the gate. */
  onboardingOutstandingCount?: number
  /**
   * The current user's resolved access subject, from `useAccessSubject()`.
   *
   * This is what makes an item's `capability` enforceable. Both nav context
   * builders (`useNavCtx` for the old UI, `useNewNavCtx` for the new) MUST
   * supply it — an item with a `capability` and no subject fails closed and
   * disappears, which is the safe direction but not a silent one you would
   * enjoy debugging.
   *
   * Undefined only outside an `AccessProvider`.
   */
  access?: AccessSubject
}

export interface NavItem {
  id: string
  label: string
  icon: NavIcon
  to?: (ctx: NavContext) => string
  /**
   * Venue-scoped URL builder. Used when a venue is active; every section with
   * a venue-level route declares it here. Always returns a
   * `/:orgId/venues/:serial/<section>` URL — no per-section URL shapes.
   */
  venueTo?: (ctx: NavContext & { serial: string }) => string
  /** Label shown when a venue is active. Falls back to `label` if omitted. */
  venueLabel?: string
  /**
   * Optional dynamic label resolver. If provided, this function is called
   * to compute the label at render time based on NavContext (e.g., to load
   * display names from data provider metadata). Falls back to `label` if
   * the resolver returns undefined.
   */
  getDynamicLabel?: (ctx: NavContext) => string | undefined
  /**
   * How the pathname is matched against `href` to decide if the item is the
   * active one. `'prefix'` (default) matches the href and anything under it;
   * `'exact'` only matches the exact pathname. Use `'exact'` for items whose
   * URL is itself a prefix of sibling items (e.g. the venue Overview at
   * /venues/:serial, which is a prefix of /venues/:serial/bookings).
   */
  activeMatch?: 'prefix' | 'exact'
  children?: NavItem[]
  /**
   * Top-level section whose children render as an INLINE accordion in the
   * main sidebar list (the SubNavAccordion pattern) instead of drilling into
   * a replacement panel. Clicking the parent from outside navigates to its
   * first child, which auto-expands the group; the route derives open state.
   */
  inlineChildren?: boolean
  /**
   * Capability required to see this item. Resolved against `ctx.access` through
   * the shared policy, so the item and the route it points at answer to the
   * same rule and cannot drift apart.
   *
   * Replaces `deniedRoles`, which was declared here, never set on a single item,
   * and never read by any consumer. The old nav's header comment claimed it
   * inherited "role gating (`deniedRoles`)" from this tree; it inherited
   * nothing, so the v4 sidebar performed no role gating at all while the old
   * sidebar still did via `useMenu`'s `denyByKey`. Role-based hiding was
   * therefore quietly disappearing as `newUi` rolled out. This field is how it
   * comes back.
   *
   * Composes with `isVisible`: BOTH must pass. Put durable rules about who a
   * user is here, and leave `isVisible` for route-shape conditions (a venue is
   * selected, a scheme is picked).
   */
  capability?: Capability
  /**
   * If true, this item is only shown when a venue is active in the URL.
   * Used for sub-nav items that only make sense in a venue context
   * (e.g. day planner, floorplan, table mappings).
   */
  requiresVenue?: boolean
  /**
   * If true, this item is a loyalty scheme-scoped service (rewards, vouchers…)
   * that only resolves once a scheme is selected. When no scheme is active it
   * links to the schemes list ("pick a scheme") and is never shown as active.
   */
  requiresScheme?: boolean
  /** Open the resolved href in a new tab (external preview links). */
  external?: boolean
  /**
   * Command item: instead of navigating, clicking dispatches this named action
   * (e.g. opens a global dialog). Rendered as a button, not a link, so it opens
   * over the current page without a route change. Takes precedence over `to`.
   */
  action?: 'create-review-page'
  /**
   * Render a visual divider above this item in sub-nav lists. Used to group
   * related children into sections (views / layout / settings / etc.).
   */
  separatorBefore?: boolean
  /**
   * Visual tone for the row. `'destructive'` paints the label + icon in the
   * destructive token (e.g. the Archived items) so they read as a distinct,
   * lower-priority group. Defaults to the normal sidebar foreground.
   */
  tone?: 'destructive'
  /**
   * Small text badge shown beside the label. `'new'` (info tint) flags a newer
   * replacement; `'degraded'` (warning tint) flags a legacy item being phased
   * out; `'stampede'` (solid brand orange) marks a surface only Stampede staff
   * can see, so it is obvious at a glance which rows a venue does not have yet.
   * Distinct from the numeric count `badges` map.
   *
   * The badge is a label, not a gate: it says nothing about who can see the
   * item. Pair `'stampede'` with the capability that actually restricts it.
   */
  badge?: { label: string; tone: 'new' | 'degraded' | 'stampede' }
  /**
   * Hide this item from the mobile nav only (still shown in the desktop
   * sidebar). Used for bookings views that don't make sense on a phone
   * (e.g. floorplan — mobile only renders the day planner). Either a static
   * boolean or a `ctx` predicate — the latter lets an item's mobile
   * visibility depend on state (e.g. List View, mobile-visible only when the
   * `listViewV3` flag routes it at the touch-first rebuild). Resolve via
   * `isHiddenOnMobile`.
   */
  hideOnMobile?: boolean | ((ctx: NavContext) => boolean)
  /**
   * The page reads `?date=YYYY-MM-DD` and opens on that day.
   *
   * Declared here so the nav manifest can carry it, which is what lets the
   * support agent take somebody to LAST SATURDAY'S day planner rather than
   * to the day planner followed by "now change the date". Only the three
   * operator views have it; everything else ignores the parameter, and
   * appending it to a page that ignores it would put a meaningless query
   * string in the customer's address bar.
   *
   * `nav-manifest.test.ts` checks this against the pages themselves: any
   * route that reads the parameter and is not marked, or is marked and does
   * not read it, fails the suite. The flag cannot quietly rot.
   */
  acceptsDate?: boolean
  isVisible?: (ctx: NavContext) => boolean
}

/** Resolve an item's URL, preferring `venueTo` when a venue is active. */
export function resolveHref(
  item: NavItem,
  ctx: NavContext
): string | undefined {
  if (ctx.serial && item.venueTo) {
    return item.venueTo({ ...ctx, serial: ctx.serial })
  }
  return item.to?.(ctx)
}

/**
 * Should this item render?
 *
 * Three gates, all of which must pass:
 *
 *   1. `requiresVenue` — a venue is in the URL.
 *   2. `capability`    — the user holds it, per the shared access policy.
 *   3. `isVisible`     — the item's own route-shape predicate.
 *
 * The capability check fails CLOSED when no subject is present, matching the
 * convention already used for `hasSquare` and `dataProviders`: an item appears
 * once we know the user may have it, rather than flashing in and being retracted.
 */
export function isNavItemVisible(item: NavItem, ctx: NavContext): boolean {
  if (item.requiresVenue && !ctx.serial) return false
  if (item.capability) {
    if (!ctx.access) return false
    if (!can(ctx.access, item.capability)) return false
  }
  return item.isVisible ? item.isVisible(ctx) : true
}

/** Resolve an item's mobile-only hide flag — a static boolean or a `ctx`
 *  predicate. Kept beside the other resolvers so the mobile nav reads it the
 *  same way everywhere. */
export function isHiddenOnMobile(item: NavItem, ctx: NavContext): boolean {
  return typeof item.hideOnMobile === 'function'
    ? item.hideOnMobile(ctx)
    : Boolean(item.hideOnMobile)
}

/** Resolve an item's label, preferring `venueLabel` when a venue is active, then dynamic label if available. */
export function resolveLabel(item: NavItem, ctx: NavContext): string {
  if (ctx.serial && item.venueLabel) return item.venueLabel
  const dynamicLabel = item.getDynamicLabel?.(ctx)
  return dynamicLabel ?? item.label
}

/**
 * List View 3 rollout gate. When on, the "List View" nav entry points at the
 * tailwind-v4 `list-view-3`; when off it stays on the live `list-view-fast`
 * (PRD D17 / ARD §9).
 *
 * This is a ROUTING choice rather than a visibility one — the item shows either
 * way, it just resolves to a different href — which is why it stays a helper
 * over `ctx` instead of becoming the item's `capability`. It reads the same
 * `bookings.list-view-3` rule the page and the route guard read, so the nav
 * cannot send someone to a surface they are then refused at.
 */
// The not-yet-enabled twin of a staff-gated item: visible ONLY to non-staff
// users who run the venue (settings holders - the people who could buy it),
// pointing at the feature's write-up. Staff see the real item; FOH and
// reservations roles see neither, keeping their menus operational-only.
//
// EXPORTED because the "what's new" login pop-up must reach exactly this
// audience and no other. A second copy of the predicate would drift, and the
// failure mode is interrupting floor staff mid-service — precisely what this
// rule exists to prevent.
export const showsComingSoon = (ctx: NavContext): boolean =>
  Boolean(
    ctx.access && !ctx.access.isStaff && can(ctx.access, 'bookings.settings')
  )

// The Voice AI write-up hides once the organisation holds the real item
// through the pilot list; otherwise a piloting customer would see the feature
// and an advert for it side by side.
const showsVoiceUpsell = (ctx: NavContext): boolean =>
  showsComingSoon(ctx) &&
  Boolean(ctx.access && !can(ctx.access, 'bookings.voice'))

const isListViewV3Enabled = (ctx: NavContext): boolean =>
  Boolean(ctx.access && can(ctx.access, 'bookings.list-view-3'))

// Primary venue-context nav: surfaced at the top of the sidebar.
export const orgNavPrimary: NavItem[] = [
  {
    // Only shown while the user has pending invitations to join an
    // organisation — a time-sensitive action, so it sits above the
    // operational nav rather than in the secondary group.
    id: 'invitations',
    label: 'Invitations',
    icon: Mail,
    to: () => '/',
    isVisible: ({ pendingInvitationsCount }) =>
      (pendingInvitationsCount ?? 0) > 0,
  },
  {
    /**
     * The account's own setup list, shown while the delivery model says
     * this account is being onboarded. Like Invitations above, a row that
     * should stop existing once it has been dealt with rather than
     * becoming permanent furniture: a finished account has no setup to
     * get on with, and the great majority of organisations never went
     * through an onboarding job at all.
     *
     * The off-ramp for the account that is genuinely never going to do
     * one of these is the admin switching that check off, which removes
     * it from the count and so from here. See morpheus's
     * `organisations/:orgId/onboarding` controller.
     */
    id: 'onboarding',
    label: 'Getting set up',
    icon: ListChecks,
    to: ({ orgId }) => `/${orgId}/onboarding`,
    isVisible: ({ onboardingNav }) => onboardingNav === true,
  },
  {
    id: 'venues',
    label: 'Venues',
    venueLabel: 'Overview',
    icon: Building2,
    capability: 'nav.venues',
    to: ({ orgId }) => `/${orgId}/venues`,
    venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}`,
    // Overview's URL is a prefix of every venue sub-page; exact-match only
    // so it doesn't light up when the user is on Bookings, Reviews, etc.
    activeMatch: 'exact',
  },
  {
    id: 'square',
    capability: 'nav.org-tools',
    label: 'Square POS',
    icon: SquareNavIcon,
    to: ({ orgId }) => `/${orgId}/square`,
    venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}/square`,
    children: [
      {
        id: 'square-overview',
        label: 'Overview',
        icon: LayoutDashboard,
        to: ({ orgId }) => `/${orgId}/square`,
        venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}/square`,
        // `/square` is a prefix of every sibling sub-page (discounts, traces,
        // table-mappings…) so exact-match only — otherwise Overview stays lit
        // on all of them.
        activeMatch: 'exact',
      },
      {
        id: 'square-table-mappings',
        label: 'Table Mappings',
        icon: TableProperties,
        // Always navigable. When no venue is scoped, the `/square/table-mappings`
        // route renders a "Choose a venue to continue" picker; once picked,
        // we forward to /venues/<serial>/square/table-mappings.
        to: ({ orgId }) => `/${orgId}/square/table-mappings`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/square/table-mappings`,
      },
      {
        id: 'square-discounts',
        label: 'Discounts',
        icon: Tag,
        // Discounts are entity-scoped, not venue-scoped, so always land on the
        // all-venues view — even from a venue-scoped context this flips the top
        // venue picker off the single venue. See docs/square-discounts.
        to: ({ orgId }) => `/${orgId}/square/discounts`,
        venueTo: ({ orgId }) => `/${orgId}/square/discounts`,
      },
      {
        // Square's own Loyalty programme (points/tiers/enrolment), read-only
        // from Stampede - never bare "Loyalty", which already names the
        // unrelated loyalty-v2 nav item above. Entity-scoped, same reasoning
        // as square-discounts. See docs/square-loyalty in the neo repo.
        id: 'square-loyalty',
        label: 'Square Loyalty',
        icon: Award,
        to: ({ orgId }) => `/${orgId}/square/loyalty`,
        venueTo: ({ orgId }) => `/${orgId}/square/loyalty`,
      },
      {
        // Manual Square customer groups (create/rename/delete), managed
        // directly from Stampede. Entity-scoped, same reasoning as
        // square-discounts / square-loyalty — groups are merchant-wide in
        // Square, not tied to a venue.
        id: 'square-customer-groups',
        label: 'Customer Groups',
        icon: Users,
        to: ({ orgId }) => `/${orgId}/square/customer-groups`,
        venueTo: ({ orgId }) => `/${orgId}/square/customer-groups`,
      },
      {
        id: 'square-traces',
        label: 'Traces',
        icon: Activity,
        // Loom traces — paste a booking/order ID, see the full chain across
        // neo + zion. When venue-scoped, pre-filters to that venue's serial.
        to: ({ orgId }) => `/${orgId}/square/traces`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/square/traces`,
      },
      {
        // Sales/info explainer — sits at the bottom of the Square POS sub-nav
        // so linked venues can always revisit "what this unlocks".
        id: 'square-what-is-this',
        label: 'What is this?',
        icon: HelpCircle,
        separatorBefore: true,
        to: ({ orgId }) => `/${orgId}/square/what-is-this`,
      },
    ],
  },
  {
    // Ordering (Mouse). Org-level: menus are an org library, orders span
    // venues, and the catalogue belongs to the Square merchant rather than a
    // venue. Two gates compose: `nav.ordering` is who-you-are (every role but
    // front of house and IT support, see access.policy.ts); `hasOrdering`
    // is Mouse's answer from the ONE capabilities hook, supplied by BOTH nav
    // ctx builders. Fails closed until Mouse confirms.
    id: 'ordering',
    capability: 'nav.ordering',
    label: 'Ordering',
    icon: UtensilsCrossed,
    // PRE-ORDERING IS WHERE ORDERING OPENS. It is the list staff work:
    // next month's bookings being built, which somebody chases. Table
    // orders are a record of what already happened at a table, and All
    // orders is the two of them mixed, which is nobody's job.
    to: ({ orgId }) => `/${orgId}/ordering/orders/pre-ordering`,
    venueTo: ({ orgId, serial }) =>
      `/${orgId}/venues/${serial}/ordering/orders/pre-ordering`,
    isVisible: ({ hasOrdering }) => hasOrdering === true,
    children: [
      {
        /**
         * ORDERS IS THREE LISTS, NOT ONE LIST AND A DROPDOWN.
         *
         * A venue that pre-orders and takes table orders met both in one
         * stream and had to narrow it by hand every time. They are different
         * jobs done by different people: a pre-order is next month's booking
         * being built, a table order is happening now.
         *
         * `activeMatch: 'exact'` on the parent and on All orders, because
         * this href is a prefix of both children and of every order's detail
         * page; prefix matching would light the whole group up at once. The
         * rule the venue Overview already follows for the same reason.
         */
        id: 'ordering-orders',
        label: 'Orders',
        icon: Receipt,
        // Same destination as the section above, for the same reason: the
        // group opens on the list somebody has work to do in.
        to: ({ orgId }) => `/${orgId}/ordering/orders/pre-ordering`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/ordering/orders/pre-ordering`,
        activeMatch: 'exact',
        children: [
          {
            id: 'ordering-orders-all',
            label: 'All orders',
            icon: Receipt,
            to: ({ orgId }) => `/${orgId}/ordering/orders`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/ordering/orders`,
            activeMatch: 'exact',
          },
          {
            id: 'ordering-orders-pre',
            label: 'Pre-ordering',
            icon: CalendarDays,
            to: ({ orgId }) => `/${orgId}/ordering/orders/pre-ordering`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/ordering/orders/pre-ordering`,
          },
          {
            id: 'ordering-orders-table',
            label: 'Table ordering',
            icon: Utensils,
            to: ({ orgId }) => `/${orgId}/ordering/orders/table-ordering`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/ordering/orders/table-ordering`,
          },
        ],
      },
      {
        id: 'ordering-menus',
        label: 'Menus',
        icon: Utensils,
        to: ({ orgId }) => `/${orgId}/ordering/menus`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/ordering/menus`,
      },
      {
        id: 'ordering-products',
        label: 'Products',
        icon: ShoppingBag,
        to: ({ orgId }) => `/${orgId}/ordering/products`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/ordering/products`,
      },
      {
        id: 'ordering-modifiers',
        label: 'Modifiers',
        icon: Boxes,
        to: ({ orgId }) => `/${orgId}/ordering/modifiers`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/ordering/modifiers`,
      },
      {
        id: 'ordering-tables',
        label: 'Tables and QR',
        icon: QrCode,
        to: ({ orgId }) => `/${orgId}/ordering/tables`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/ordering/tables`,
      },
      {
        // Moved here from Bookings. The cards describe what a guest ORDERED,
        // so they belong with ordering rather than with the reservation.
        //
        // NOTE the gating that comes with the move: this section is
        // `nav.ordering`, which is `roleNotIn('front-of-house', 'it-support')`,
        // and it is hidden until Mouse confirms `hasOrdering`. Under Bookings
        // this was deliberately `bookings.views` so front of house - the people
        // who lay the tables before service - could print blank cards without
        // any pre-order access at all (ARD §9). They no longer see it.
        id: 'ordering-placecards',
        label: 'Place cards',
        icon: Printer,
        to: ({ orgId }) => `/${orgId}/ordering/placecards`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/ordering/placecards`,
      },
      {
        // Every text the ordering service sent, and what became of it.
        // Under Ordering rather than Marketing: these are the order texts
        // (sent to the kitchen, ready to collect), not a campaign.
        id: 'ordering-sms',
        label: 'SMS',
        icon: MessageSquare,
        to: ({ orgId }) => `/${orgId}/ordering/sms`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/ordering/sms`,
      },
      {
        // STAMPEDE ONLY, and it has to stay that way. Guests are told on
        // the confirmation screen that their words about the app come to
        // us rather than to the venue; this capability is the whole of how
        // that promise is kept. It is not a review of the venue or its
        // food, and no customer should ever see it or the link to it.
        id: 'ordering-app-feedback',
        capability: 'internal.staff',
        label: 'App feedback',
        icon: MessageSquareQuote,
        to: ({ orgId }) => `/${orgId}/ordering/app-feedback`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/ordering/app-feedback`,
      },
    ],
  },
  {
    id: 'bookings',
    label: 'Bookings',
    icon: BookOpen,
    capability: 'nav.bookings',
    to: ({ orgId }) => `/${orgId}/bookings`,
    venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}/bookings`,
    children: [
      // Views — the day-to-day operator surfaces.
      {
        id: 'bookings-day-planner',
        acceptsDate: true,
        capability: 'bookings.views',
        label: 'Day Planner',
        icon: Calendar,
        to: ({ orgId }) => `/${orgId}/bookings/day-planner-3`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/day-planner-3`,
      },
      {
        id: 'bookings-list',
        acceptsDate: true,
        capability: 'bookings.views',
        label: 'List View',
        icon: List,
        // Repointed to the tailwind-v4 list-view-3 for staff with the
        // `listViewV3` flag on; everyone else stays on list-view-fast until
        // cutover (PRD D17 / ARD §9).
        to: (ctx) =>
          isListViewV3Enabled(ctx)
            ? `/${ctx.orgId}/bookings/list-view-3`
            : `/${ctx.orgId}/bookings/list-view-fast`,
        venueTo: (ctx) =>
          isListViewV3Enabled(ctx)
            ? `/${ctx.orgId}/venues/${ctx.serial}/bookings/list-view-3`
            : `/${ctx.orgId}/venues/${ctx.serial}/bookings/list-view-fast`,
        // Mobile: list-view-3 is touch-first (PRD D2), so show it on mobile
        // when the flag routes there; flag-off users still land on the
        // desktop-oriented list-view-fast, so keep it hidden on mobile for them.
        hideOnMobile: (ctx) => !isListViewV3Enabled(ctx),
      },
      {
        id: 'bookings-floorplan',
        acceptsDate: true,
        capability: 'bookings.views',
        label: 'Floorplan',
        icon: LayoutGrid,
        // Mobile renders only the day planner; floorplan is desktop-only.
        hideOnMobile: true,
        to: ({ orgId }) => `/${orgId}/bookings/floorplan-3`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/floorplan-3`,
      },
      {
        id: 'bookings-floorplan-edit',
        capability: 'bookings.floorplan.edit',
        label: 'Floorplan Editor',
        icon: Pencil,
        to: ({ orgId }) => `/${orgId}/bookings/floorplan-3-edit`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/floorplan-3-edit`,
      },
      {
        id: 'bookings-enquiries',
        capability: 'bookings.enquiries',
        label: 'Enquiries',
        icon: MessageSquare,
        // Accordion group: the parent lands on the Inbox (first child) and
        // auto-expands, same shape as Main Settings / reviews → AI Settings.
        to: ({ orgId }) => `/${orgId}/bookings/enquiries`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/enquiries`,
        children: [
          {
            id: 'bookings-enquiries-inbox',
            capability: 'bookings.enquiries',
            label: 'Inbox',
            icon: Inbox,
            // Exact so it doesn't also light on /enquiries/reports or /:id.
            activeMatch: 'exact',
            to: ({ orgId }) => `/${orgId}/bookings/enquiries`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/enquiries`,
          },
          {
            id: 'bookings-enquiries-reports',
            capability: 'bookings.reports',
            label: 'Reports',
            icon: BarChart3,
            to: ({ orgId }) => `/${orgId}/bookings/enquiries/reports`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/enquiries/reports`,
          },
          {
            id: 'bookings-enquiries-settings',
            capability: 'bookings.settings',
            label: 'Settings',
            icon: Settings,
            to: ({ orgId }) => `/${orgId}/bookings/settings/enquiries`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/enquiries`,
          },
          {
            // Enquiries workspace (zion ENQUIRIES_WORKSPACE_ARD.md D17): the full-page
            // app with its own rail. Staff plus the named early-access users listed
            // in the policy while it is built; the route carries the same capability,
            // which is the real gate. Widen its rule to 'bookings.enquiries' to
            // release it. Badged 'Early access', not 'Stampede only': an allow-listed
            // customer sees this row too.
            id: 'bookings-enquiries-workspace',
            capability: 'bookings.enquiries-workspace',
            label: 'Workspace',
            icon: LayoutGrid,
            badge: { label: 'Early access', tone: 'new' },
            to: ({ orgId }) => `/${orgId}/bookings/enquiries-workspace`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/enquiries-workspace`,
          },
        ],
      },

      // Layout — the physical room.
      {
        id: 'bookings-areas',
        capability: 'bookings.layout',
        label: 'Areas',
        icon: Building2,
        to: ({ orgId }) => `/${orgId}/bookings/settings/areas`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/settings/areas`,
        separatorBefore: true,
      },
      {
        id: 'bookings-tables',
        capability: 'bookings.layout',
        label: 'Tables',
        icon: TableProperties,
        to: ({ orgId }) => `/${orgId}/bookings/settings/tables`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/settings/tables`,
      },
      {
        id: 'bookings-combinations',
        capability: 'bookings.layout',
        label: 'Combinations',
        icon: Combine,
        to: ({ orgId }) => `/${orgId}/bookings/settings/combinations`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/settings/combinations`,
      },

      // Settings — configuration surfaces.
      {
        id: 'bookings-main-settings',
        capability: 'bookings.settings',
        label: 'Main Settings',
        icon: Settings,
        // Parent lands on the first section; accordion auto-expands here
        // (same shape as reviews → AI Settings). One child per section of
        // the main-configuration form — each is its own page.
        separatorBefore: true,
        to: ({ orgId }) =>
          `/${orgId}/bookings/settings/main-configuration/basic`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/settings/main-configuration/basic`,
        children: [
          {
            id: 'bookings-config-basic',
            label: 'Basic',
            icon: Home,
            to: ({ orgId }) =>
              `/${orgId}/bookings/settings/main-configuration/basic`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/main-configuration/basic`,
          },
          {
            id: 'bookings-config-party-sizes',
            label: 'Party Sizes',
            icon: Users,
            to: ({ orgId }) =>
              `/${orgId}/bookings/settings/main-configuration/party-sizes`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/main-configuration/party-sizes`,
          },
          {
            id: 'bookings-config-opening-hours',
            label: 'Bookable Times',
            icon: Sun,
            to: ({ orgId }) =>
              `/${orgId}/bookings/settings/main-configuration/opening-hours`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/main-configuration/opening-hours`,
          },
          {
            id: 'bookings-config-booking-options',
            label: 'Booking Options',
            icon: Settings2,
            to: ({ orgId }) =>
              `/${orgId}/bookings/settings/main-configuration/booking-options`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/main-configuration/booking-options`,
          },
          {
            id: 'bookings-config-payments',
            label: 'Stripe',
            icon: CreditCard,
            to: ({ orgId }) =>
              `/${orgId}/bookings/settings/main-configuration/payments`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/main-configuration/payments`,
          },
          {
            id: 'bookings-config-charges',
            label: 'Payments',
            icon: Wallet,
            to: ({ orgId }) =>
              `/${orgId}/bookings/settings/main-configuration/charges`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/main-configuration/charges`,
          },
          {
            id: 'bookings-config-payment-reminders',
            label: 'Payment Reminders',
            icon: BellRing,
            to: ({ orgId }) => `/${orgId}/bookings/settings/payment-reminders`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/payment-reminders`,
          },
          {
            id: 'bookings-config-communications',
            label: 'Communications',
            icon: Bell,
            to: ({ orgId }) =>
              `/${orgId}/bookings/settings/main-configuration/communications`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/main-configuration/communications`,
          },
          {
            id: 'bookings-config-day-planner',
            label: 'Day Planner',
            icon: CalendarDays,
            to: ({ orgId }) =>
              `/${orgId}/bookings/settings/main-configuration/day-planner`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/main-configuration/day-planner`,
          },
          {
            id: 'bookings-config-guest-preferences',
            label: 'Guest Preferences',
            icon: Sparkles,
            to: ({ orgId }) =>
              `/${orgId}/bookings/settings/main-configuration/guest-preferences`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/main-configuration/guest-preferences`,
          },
          {
            id: 'bookings-config-capacity',
            label: 'Cover Limits',
            icon: Gauge,
            to: ({ orgId }) =>
              `/${orgId}/bookings/settings/main-configuration/capacity`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/main-configuration/capacity`,
          },
          {
            id: 'bookings-config-upgrades',
            label: 'Attach upgrades',
            icon: Gift,
            to: ({ orgId }) =>
              `/${orgId}/bookings/settings/main-configuration/upgrades`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/main-configuration/upgrades`,
          },
          {
            id: 'bookings-config-menus',
            label: 'Attach menus',
            icon: UtensilsCrossed,
            to: ({ orgId }) =>
              `/${orgId}/bookings/settings/main-configuration/menus`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/main-configuration/menus`,
          },
        ],
      },
      {
        // The guest-facing landing-page surface (replaces "Design"). The
        // parent lands on Settings; the accordion auto-expands (same shape
        // as Main Settings) — the sub-nav replaced the old in-page tabs.
        id: 'bookings-landing-page',
        capability: 'bookings.settings',
        label: 'Landing Page',
        icon: Newspaper,
        to: ({ orgId }) => `/${orgId}/bookings/design`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/design`,
        children: [
          {
            id: 'bookings-landing-page-settings',
            label: 'Settings',
            icon: Settings2,
            activeMatch: 'exact',
            to: ({ orgId }) => `/${orgId}/bookings/design`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/design`,
          },
          {
            id: 'bookings-landing-page-branding',
            label: 'Branding',
            icon: Palette,
            to: ({ orgId }) => `/${orgId}/bookings/design/branding`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/design/branding`,
          },
        ],
      },
      {
        id: 'bookings-booking-types',
        capability: 'bookings.settings',
        label: 'Booking Types',
        icon: Tag,
        to: ({ orgId }) => `/${orgId}/bookings/settings/booking-types`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/settings/booking-types`,
      },
      {
        id: 'bookings-special-dates',
        capability: 'bookings.settings',
        label: 'Special Dates',
        icon: CalendarClock,
        to: ({ orgId }) => `/${orgId}/bookings/settings/special-dates`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/settings/special-dates`,
      },
      // One Events group with the two kinds beneath it, rather than two
      // top-level rows. They belong together in the sidebar because a venue
      // thinks "I am putting an event on", but they are separate destinations
      // because they share almost none of their configuration: a table event
      // blocks booking types across opening hours, a ticketed event sells
      // inventory and has a door scanner behind it.
      //
      // While Ticketed Events is in early access this group renders with a
      // single child for most venues. That resolves itself on release; the
      // alternative was hiding the grouping until then, which would move the
      // row twice.
      {
        id: 'bookings-events',
        capability: 'bookings.settings',
        label: 'Events',
        icon: Sparkles,
        // Parent lands on Booking Events, the first child, which auto-expands
        // the accordion (same shape as Main Settings). Each child repeats its
        // own capability: it does not inherit.
        to: ({ orgId }) => `/${orgId}/bookings/settings/events`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/settings/events`,
        children: [
          {
            id: 'bookings-events-booking',
            capability: 'bookings.settings',
            label: 'Booking Events',
            icon: Sparkles,
            to: ({ orgId }) => `/${orgId}/bookings/settings/events`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/events`,
          },
          {
            id: 'bookings-ticketed-events',
            // Released: the capability now resolves to 'bookings.settings'.
            capability: 'bookings.ticketed-events',
            label: 'Ticketed Events',
            icon: Ticket,
            to: ({ orgId }) => `/${orgId}/bookings/settings/ticketed-events`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/ticketed-events`,
          },
        ],
      },

      // Product — what guests can order / add.
      {
        id: 'bookings-upgrades',
        capability: 'bookings.settings',
        label: 'Upgrades',
        icon: ArrowUp,
        to: ({ orgId }) => `/${orgId}/bookings/settings/upgrades`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/settings/upgrades`,
        separatorBefore: true,
      },
      {
        // Smart Seating (zion TABLE_SHUFFLE_ARD.md D14): the explainer, the
        // replay test and the shadow toggle. Customer-visible - the page
        // itself is the teaser; the serving switch ships with Bookings Plus.
        // Venue-scoped only: the replay runs against one venue's book, so
        // there is no org-level page.
        id: 'bookings-optimisation',
        capability: 'bookings.settings',
        label: 'Smart Seating',
        icon: Sparkles,
        requiresVenue: true,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/optimisation`,
      },
      {
        // `WalletCards` rather than `Wallet`: the Charges accordion already uses
        // `Wallet` for Deposits, and two identical glyphs in one sidebar read as
        // the same destination.
        id: 'bookings-wallet',
        // Released (October 2026) to anyone who can see bookings Settings.
        capability: 'bookings.settings',
        label: 'Wallets',
        icon: WalletCards,
        // Parent lands on Settings, the first child, which auto-expands the
        // accordion (same shape as Menus). Each child repeats the capability:
        // it does not inherit, and `isNavItemVisible` is applied per item.
        to: ({ orgId }) => `/${orgId}/bookings/settings/wallet`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/settings/wallet`,
        children: [
          {
            id: 'bookings-wallet-settings',
            capability: 'bookings.settings',
            label: 'Settings',
            icon: Settings2,
            // Exact, or the parent route keeps this lit on Passes and
            // Downloads, both of which sit beneath it.
            activeMatch: 'exact',
            to: ({ orgId }) => `/${orgId}/bookings/settings/wallet`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/wallet`,
          },
          {
            id: 'bookings-wallet-passes',
            capability: 'bookings.settings',
            label: 'Passes',
            icon: Ticket,
            to: ({ orgId }) => `/${orgId}/bookings/settings/wallet/passes`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/wallet/passes`,
          },
          {
            id: 'bookings-wallet-downloads',
            capability: 'bookings.settings',
            label: 'Downloads',
            icon: Download,
            to: ({ orgId }) => `/${orgId}/bookings/settings/wallet/downloads`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/settings/wallet/downloads`,
          },
        ],
      },
      {
        id: 'bookings-voice',
        // Staff plus the piloting organisations listed in the policy while
        // the AI voice agent is proved. Widen the capability's rule to
        // 'bookings.settings' to release it. Badged 'Early access', not
        // 'Stampede only': a piloting customer sees this row too.
        capability: 'bookings.voice',
        label: 'Voice AI',
        icon: Phone,
        badge: { label: 'Early access', tone: 'new' },
        // Parent lands on Settings, the first child, which auto-expands the
        // accordion (same shape as Wallets). Each child repeats the
        // capability: it does not inherit.
        to: ({ orgId }) => `/${orgId}/bookings/voice`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/voice`,
        children: [
          {
            id: 'bookings-voice-settings',
            capability: 'bookings.voice',
            label: 'Settings',
            icon: Settings,
            // Exact so it does not also light on /voice/calls.
            activeMatch: 'exact',
            to: ({ orgId }) => `/${orgId}/bookings/voice`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/voice`,
          },
          {
            id: 'bookings-voice-calls',
            capability: 'bookings.voice',
            label: 'Call transcripts',
            icon: FileText,
            to: ({ orgId }) => `/${orgId}/bookings/voice/calls`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/voice/calls`,
          },
          {
            id: 'bookings-voice-reporting',
            capability: 'bookings.voice',
            label: 'Reporting',
            icon: BarChart3,
            to: ({ orgId }) => `/${orgId}/bookings/voice/reporting`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/voice/reporting`,
          },
        ],
      },

      // What a non-staff user sees in place of the Voice AI pilot item above:
      // a detailed write-up. Wallets and Ticketed Events used to sit here too
      // and were released in October 2026, so their rows are gone.
      //
      // Badged 'New', not 'Coming soon': it is built and released, and the
      // marketing site and the sign-in both say so. A product still saying
      // "coming soon" about a feature the website is inviting you to ask for is
      // a contradiction the customer can see.
      {
        id: 'bookings-voice-upsell',
        isVisible: showsVoiceUpsell,
        label: 'Voice AI',
        icon: Phone,
        badge: { label: 'New', tone: 'new' },
        to: ({ orgId }) => `/${orgId}/bookings/upsell/voice`,
        venueTo: ({ orgId }) => `/${orgId}/bookings/upsell/voice`,
      },

      // Ops — cross-venue operations.
      {
        id: 'bookings-deposits',
        capability: 'bookings.deposits',
        // Renamed from "Deposits": the page now covers cancellation charges and
        // enquiry money too, and "Deposits" also names a Full Bhuna report,
        // which made the old label ambiguous in search.
        label: 'Charges',
        icon: CreditCard,
        // The route is unchanged so existing links keep working. The children
        // are the same page under a preset type filter rather than pages of
        // their own: the columns and the row action are identical across charge
        // kinds, so splitting them would duplicate a table to hardcode a filter.
        to: ({ orgId }) => `/${orgId}/bookings/deposits`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/deposits`,
        separatorBefore: true,
        children: [
          {
            id: 'bookings-charges-all',
            capability: 'bookings.deposits',
            label: 'All charges',
            icon: CreditCard,
            // Exact, so it does not also light up on the filtered children.
            activeMatch: 'exact',
            to: ({ orgId }) => `/${orgId}/bookings/deposits`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/deposits`,
          },
          {
            id: 'bookings-charges-deposits',
            capability: 'bookings.deposits',
            label: 'Deposits',
            icon: Wallet,
            to: ({ orgId }) =>
              `/${orgId}/bookings/deposits?type=BOOKING_DEPOSIT,ENQUIRY_DEPOSIT`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/deposits?type=BOOKING_DEPOSIT,ENQUIRY_DEPOSIT`,
          },
          {
            id: 'bookings-charges-cancellations',
            capability: 'bookings.deposits',
            label: 'Cancellation charges',
            icon: CreditCard,
            to: ({ orgId }) =>
              `/${orgId}/bookings/deposits?type=BOOKING_CANCELLATION_CHARGE,ENQUIRY_CANCELLATION_CHARGE`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/bookings/deposits?type=BOOKING_CANCELLATION_CHARGE,ENQUIRY_CANCELLATION_CHARGE`,
          },
        ],
      },
      {
        id: 'bookings-reporting',
        capability: 'bookings.reports',
        label: 'Reporting',
        icon: PieChart,
        to: ({ orgId }) => `/${orgId}/bookings/reports`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/reports`,
      },
      {
        id: 'bookings-outstanding-payments',
        capability: 'bookings.outstanding-payments',
        label: 'Outstanding Payments',
        icon: CreditCard,
        to: ({ orgId }) => `/${orgId}/bookings/outstanding-payments`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/outstanding-payments`,
      },
      {
        id: 'bookings-booking-alerts',
        capability: 'bookings.settings',
        label: 'Booking Alerts',
        icon: BellRing,
        to: ({ orgId }) => `/${orgId}/bookings/booking-alerts`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/booking-alerts`,
      },
    ],
  },
  {
    id: 'reviews',
    label: 'Reviews',
    icon: Star,
    capability: 'nav.reviews',
    to: ({ orgId }) => `/${orgId}/reviews`,
    venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}/reviews`,
    children: [
      {
        id: 'reviews-responses',
        label: 'Responses',
        icon: Star,
        to: ({ orgId }) => `/${orgId}/reviews/responses`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reviews/responses`,
      },
      {
        id: 'reviews-reports',
        label: 'Reports',
        icon: Star,
        // Parent lands on the first child; the accordion auto-expands, the
        // same shape AI Settings below already uses.
        to: ({ orgId }) => `/${orgId}/reviews/reports`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reviews/reports`,
        children: [
          {
            id: 'reviews-reports-overview',
            label: 'Overview',
            icon: Star,
            // Exact, or it lights up on Metrics too.
            activeMatch: 'exact',
            to: ({ orgId }) => `/${orgId}/reviews/reports`,
            // In the VENUE context this same entry is that venue's own
            // report, because the serial is already known. In the org context
            // the venue breakdown is reached from the scorecard rows, since a
            // serial cannot be a static nav child.
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/reviews/reports`,
          },
          {
            id: 'reviews-reports-metrics',
            label: 'Metrics',
            icon: Star,
            to: ({ orgId }) => `/${orgId}/reviews/reports/metrics`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/reviews/reports/metrics`,
          },
        ],
      },
      {
        id: 'reviews-google',
        label: 'Google',
        icon: Star,
        to: ({ orgId }) => `/${orgId}/reviews/google`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reviews/google`,
      },
      {
        id: 'reviews-ai-settings',
        label: 'AI Settings',
        icon: Star,
        // Parent lands on the first child; accordion auto-expands here.
        to: ({ orgId }) => `/${orgId}/reviews/ai-settings/auto-generation`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reviews/ai-settings/auto-generation`,
        children: [
          {
            id: 'reviews-ai-auto-generation',
            label: 'Auto-Generation Rules',
            icon: Sparkles,
            to: ({ orgId }) => `/${orgId}/reviews/ai-settings/auto-generation`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/reviews/ai-settings/auto-generation`,
          },
          {
            id: 'reviews-ai-approval-queue',
            label: 'Approval Queue',
            icon: Inbox,
            to: ({ orgId }) => `/${orgId}/reviews/ai-settings/approval-queue`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/reviews/ai-settings/approval-queue`,
          },
          {
            id: 'reviews-ai-response-prompts',
            label: 'Response Prompts',
            icon: MessageSquare,
            to: ({ orgId }) => `/${orgId}/reviews/ai-settings/response-prompts`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/reviews/ai-settings/response-prompts`,
          },
          {
            id: 'reviews-ai-activity-log',
            label: 'Activity Log',
            icon: FileClock,
            capability: 'internal.staff',
            to: ({ orgId }) => `/${orgId}/reviews/ai-settings/activity-log`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/reviews/ai-settings/activity-log`,
          },
          {
            id: 'reviews-ai-analytics',
            label: 'Analytics',
            icon: BarChart3,
            to: ({ orgId }) => `/${orgId}/reviews/ai-settings/analytics`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/reviews/ai-settings/analytics`,
          },
        ],
      },
    ],
  },
  {
    id: 'loyalty',
    label: 'Loyalty',
    icon: Heart,
    capability: 'nav.loyalty',
    to: ({ orgId }) => `/${orgId}/loyalty`,
    venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}/loyalty`,
    children: [
      {
        id: 'loyalty-reports',
        label: 'Reports',
        icon: Heart,
        to: ({ orgId }) => `/${orgId}/loyalty/reports`,
      },
    ],
  },
  {
    id: 'wi-fi',
    label: 'Wi-Fi',
    icon: Wifi,
    capability: 'nav.wi-fi',
    to: ({ orgId }) => `/${orgId}/wi-fi`,
    venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}/wi-fi`,
    children: [
      {
        id: 'wifi-surveys',
        label: 'Captive Portal Surveys',
        icon: ClipboardList,
        // Venue-only — at org scope the link lands on the venue-picker gate,
        // then continues to the per-venue survey page.
        to: ({ orgId }) =>
          `/${orgId}/wi-fi/choose-venue?next=${encodeURIComponent(
            'wi-fi/survey'
          )}&label=${encodeURIComponent('Captive Portal Surveys')}`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/survey`,
        // Surveys run on the free captive-portal journey — not valid for a
        // paid network, which shows Portal Payments instead.
        isVisible: (ctx) => !isPaidVenue(ctx.venue),
      },
      {
        id: 'wifi-upsell',
        label: 'Captive Portal Upsell',
        icon: Zap,
        to: ({ orgId }) =>
          `/${orgId}/wi-fi/choose-venue?next=${encodeURIComponent(
            'wi-fi/upsell'
          )}&label=${encodeURIComponent('Captive Portal Upsell')}`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/upsell`,
      },
      {
        id: 'wifi-payments',
        label: 'Portal Payments',
        icon: CreditCard,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/payments`,
        requiresVenue: true,
        isVisible: (ctx) => isPaidVenue(ctx.venue),
      },
      {
        id: 'wifi-infrastructure',
        label: 'Infrastructure',
        icon: Wifi,
        to: ({ orgId }) => `/${orgId}/wi-fi/infrastructure`,
      },
      {
        id: 'wifi-insights',
        label: 'Captive Insights',
        icon: BarChart3,
        separatorBefore: true,
        // Venue-only — at org scope the link lands on the venue-picker gate,
        // then continues to the per-venue insight page.
        to: ({ orgId }) =>
          `/${orgId}/wi-fi/choose-venue?next=${encodeURIComponent(
            'wi-fi/insight'
          )}&label=${encodeURIComponent('Captive Insights')}`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/insight`,
      },
      // Hardware groups (SnapGuard / Hardware) — shared with the old-UI nav.
      ...WIFI_HARDWARE_GROUPS,
      {
        id: 'wifi-firewall',
        label: 'Firewall',
        icon: ShieldCheck,
        // Advanced: MikroTik firewall management (port forwarding, block/allow
        // lists). The page itself gates on the device being reachable.
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/firewall`,
        requiresVenue: true,
      },
      {
        id: 'wifi-surveys',
        label: 'Surveys',
        icon: Wifi,
        // Venue-only — at org scope the link lands on the venue-picker gate,
        // then continues to the per-venue survey page.
        to: ({ orgId }) =>
          `/${orgId}/wi-fi/choose-venue?next=${encodeURIComponent(
            'wi-fi/survey'
          )}&label=${encodeURIComponent('Surveys')}`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/survey`,
      },
      {
        id: 'wifi-upsell',
        label: 'Upsell',
        icon: Wifi,
        to: ({ orgId }) =>
          `/${orgId}/wi-fi/choose-venue?next=${encodeURIComponent(
            'wi-fi/upsell'
          )}&label=${encodeURIComponent('Upsell')}`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/wi-fi/upsell`,
      },
    ],
  },
  {
    id: 'gifting',
    label: 'Gifting',
    icon: Gift,
    // `gift-cards` is the section key this has always been gated by in
    // `denyByKey` — moderator and below cannot reach it.
    capability: 'nav.gift-cards',
    to: ({ orgId }) => `/${orgId}/gifting`,
  },
  {
    /**
     * Customers: finding a guest, and understanding them in aggregate.
     *
     * Always a group, never a bare link, so a Square org and a non-Square org
     * see the same place: every role gets Search, and Know your customer
     * appears beneath it once the org has the till data it reads from.
     *
     * The parent shares Search's href, like every other section's landing
     * child, so the sidebar opens this group on the customers page and on
     * `/customer/:id` profiles too (prefix match), with Search lit.
     *
     * No `venueTo` on Search. The customers page does filter by `?serial=`,
     * but the sidebar matches on pathname only, so a query-string href would
     * never read as active. Search stays org-wide at either scope.
     */
    id: 'customers',
    label: 'Customers',
    icon: Users,
    capability: 'nav.customers',
    to: ({ orgId }) => `/${orgId}/customer`,
    // Inline accordion in the main sidebar, as Inbox is (no drill-in panel):
    // clicking Customers lands on Search and the group expands in place.
    inlineChildren: true,
    children: [
      {
        id: 'customers-search',
        label: 'Search',
        icon: Search,
        to: ({ orgId }) => `/${orgId}/customer`,
      },
      {
        id: 'customers-know-your-customer',
        label: 'Know your customer',
        icon: Lightbulb,
        capability: 'kyc.view',
        to: ({ orgId }) => `/${orgId}/know-your-customer`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/know-your-customer`,
      },
    ],
  },
]

// Secondary nav: broader org-wide tools, separated from the primary group.
//
// The `stampedeOnly`, `isFullBhuna` and `hasDataProvider` predicates that used
// to live here are gone. They are now capabilities — `internal.staff`,
// `reports.full-bhuna` and `marketing.audiences.classic` / `.new` — in
// `src/access/access.policy.ts`, so the same rule answers for a nav item, the
// route it points at and the page's own conditionals. Their fail-closed
// behaviour is unchanged: a subject whose queries are still in flight holds
// nothing, so a gated item appears once confirmed rather than flashing in and
// being retracted.

/** Resolve the display label for a data provider from its backend metadata. */
const getDataProviderLabel = (
  providerId: string,
  fallback: string
): ((ctx: NavContext) => string) => {
  return ({ dataProviderMetadata }: NavContext) => {
    const metadata = dataProviderMetadata?.[providerId]
    return metadata?.label ?? fallback
  }
}

export const orgNavSecondary: NavItem[] = [
  {
    id: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    capability: 'nav.marketing',
    to: ({ orgId }) => `/${orgId}/marketing`,
    children: [
      // Campaigns
      {
        id: 'marketing-email-campaigns',
        label: 'Email Campaigns',
        icon: Mail,
        to: ({ orgId }) => `/${orgId}/marketing/email/campaigns`,
        // Sub-nav children default to exact-match, but the
        // campaigns route has detail / editor pages underneath
        // (`.../campaigns/:id`). Prefix-match keeps this item
        // highlighted while the operator is anywhere in the
        // campaigns surface, not only on the list page.
        activeMatch: 'prefix',
        separatorBefore: true,
      },
      {
        id: 'marketing-templates',
        label: 'Email Templates',
        icon: LayoutTemplate,
        to: ({ orgId }) => `/${orgId}/marketing/email/templates`,
      },

      // Audiences
      {
        id: 'marketing-audiences',
        // Fixed name — do NOT use getDynamicLabel here; the provider metadata
        // label would override it. This surface is always "Classic Audiences".
        label: 'Classic Audiences',
        icon: Users,
        to: ({ orgId }) => `/${orgId}/marketing/data`,
        activeMatch: 'exact',
        separatorBefore: true,
        capability: 'marketing.audiences.classic',
        badge: { label: 'Deprecated', tone: 'degraded' },
      },
      {
        id: 'marketing-simple-segments',
        label: 'Audiences',
        icon: Users,
        to: ({ orgId }) => `/${orgId}/marketing/audiences`,
        capability: 'marketing.audiences.new',
        getDynamicLabel: getDataProviderLabel(
          'morpheus_simple_segments',
          'Audiences'
        ),
        badge: { label: 'New', tone: 'new' },
      },
      {
        id: 'marketing-meta',
        label: 'Meta',
        icon: Facebook,
        to: ({ orgId }) => `/${orgId}/marketing/data/meta`,
      },
      {
        id: 'marketing-data-exports',
        label: 'Data Exports',
        icon: Download,
        to: ({ orgId }) => `/${orgId}/marketing/data/segment-data-exports`,
      },

      // Automation
      {
        id: 'marketing-workflows',
        label: 'Workflow Automations',
        icon: Zap,
        to: ({ orgId }) => `/${orgId}/marketing/email/workflows`,
        separatorBefore: true,
      },

      // Website
      {
        id: 'marketing-forms',
        label: 'Forms',
        icon: ClipboardList,
        to: ({ orgId }) => `/${orgId}/marketing/website/forms`,
        separatorBefore: true,
      },

      // SMS
      {
        id: 'marketing-sms-campaigns',
        label: 'SMS Campaigns',
        icon: MessageSquare,
        to: ({ orgId }) => `/${orgId}/marketing/sms/campaigns`,
        activeMatch: 'prefix',
        separatorBefore: true,
      },
      {
        id: 'marketing-sms-senders',
        label: 'SMS Senders',
        icon: UserCheck,
        to: ({ orgId }) => `/${orgId}/marketing/sms/senders`,
      },
      {
        id: 'marketing-sms-ledger',
        label: 'SMS Ledger',
        icon: Receipt,
        to: ({ orgId }) => `/${orgId}/marketing/sms/ledger`,
      },

      // Links and reports
      {
        id: 'marketing-link-manager',
        label: 'Link Manager',
        icon: Link2,
        to: ({ orgId }) => `/${orgId}/marketing/website/links`,
        activeMatch: 'exact',
        separatorBefore: true,
      },
      {
        id: 'marketing-utm-planner',
        label: 'UTM Planner',
        icon: Hash,
        to: ({ orgId }) => `/${orgId}/marketing/website/links/utm-planner`,
      },
      {
        id: 'marketing-conversions',
        label: 'Conversions',
        icon: Target,
        to: ({ orgId }) => `/${orgId}/marketing/website/links/conversions`,
      },
      {
        id: 'marketing-link-reports',
        label: 'Link Reports',
        icon: BarChart3,
        to: ({ orgId }) => `/${orgId}/marketing/website/links/link-reports`,
      },

      // Setup
      {
        id: 'marketing-vouchers',
        label: 'Vouchers',
        icon: Ticket,
        to: ({ orgId }) => `/${orgId}/marketing/foh/vouchers`,
        separatorBefore: true,
      },
      {
        id: 'marketing-csv-uploads',
        label: 'CSV Uploads',
        icon: FileUp,
        to: ({ orgId }) => `/${orgId}/marketing/data/upload`,
      },
      {
        id: 'marketing-domains',
        label: 'Domains',
        icon: AtSign,
        to: ({ orgId }) => `/${orgId}/marketing/email/domains`,
      },
    ],
  },
  {
    id: 'inbox',
    label: 'Inbox',
    icon: Inbox,
    capability: 'nav.inbox',
    to: ({ orgId }) => `/${orgId}/inbox/threads`,
    // Venue inbox is a real route; the venue picker swaps the serial natively
    // and the page locks the filter to that venue.
    venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}/inbox`,
    // Inline accordion in the main sidebar (no drill-in panel): clicking
    // Inbox lands on Conversations and the group expands in place.
    inlineChildren: true,
    children: [
      {
        id: 'inbox-conversations',
        label: 'Conversations',
        icon: MessageSquare,
        // Exact: the old UI rewrites this href to `/inbox` (oldifyHref),
        // which is a path prefix of Templates/Media — prefix matching would
        // light Conversations on those routes too.
        activeMatch: 'exact',
        to: ({ orgId }) => `/${orgId}/inbox/threads`,
        venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}/inbox`,
      },
      {
        id: 'inbox-templates',
        label: 'Templates',
        icon: FileText,
        to: ({ orgId }) => `/${orgId}/inbox/templates`,
      },
      {
        id: 'inbox-media',
        label: 'Media',
        icon: Paperclip,
        to: ({ orgId }) => `/${orgId}/inbox/media`,
      },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: PieChart,
    capability: 'nav.reports',
    /**
     * The Full Bhuna sections ARE this menu for a Square org.
     *
     * They used to be a nested "Full Bhuna" group inside a list of thirteen
     * other reports, which buried the report we actually want people in behind
     * an extra click and a wall of legacy pages. Now they sit one level up and
     * the legacy entries (Receipts, Origin, Transactions, Tags, Survey, Email,
     * SMS, Gift Cards, Venue Snapshot, Organisation beta) are gone from the
     * nav. Their ROUTES still exist and still resolve, so any bookmark or deep
     * link keeps working: they are simply no longer advertised.
     *
     * Customers and Interactions are the two that always show, at either
     * scope, Square or not. An org without Square therefore sees exactly those
     * two rather than an empty menu.
     */
    to: (ctx) =>
      ctx.hasSquare
        ? `/${ctx.orgId}/reports/full-bhuna`
        : `/${ctx.orgId}/reports/customers`,
    venueTo: (ctx) =>
      ctx.hasSquare
        ? `/${ctx.orgId}/venues/${ctx.serial}/reports/full-bhuna`
        : `/${ctx.orgId}/venues/${ctx.serial}/reports/customers`,
    children: [
      // ── Full Bhuna (Square orgs only) ──
      {
        id: 'reports-full-bhuna-overview',
        label: 'Overview',
        icon: LayoutDashboard,
        capability: 'reports.full-bhuna',
        // Exact, so it doesn't also light up on every sibling section.
        activeMatch: 'exact',
        to: ({ orgId }) => `/${orgId}/reports/full-bhuna`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reports/full-bhuna`,
      },
      {
        id: 'reports-full-bhuna-bookings',
        label: 'Bookings',
        icon: CalendarDays,
        capability: 'reports.full-bhuna',
        to: ({ orgId }) => `/${orgId}/reports/full-bhuna/bookings`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reports/full-bhuna/bookings`,
      },
      {
        id: 'reports-full-bhuna-revenue',
        label: 'Revenue',
        icon: CreditCard,
        capability: 'reports.full-bhuna',
        to: ({ orgId }) => `/${orgId}/reports/full-bhuna/revenue`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reports/full-bhuna/revenue`,
      },
      {
        id: 'reports-full-bhuna-deposits',
        label: 'Deposits',
        icon: Wallet,
        capability: 'reports.full-bhuna',
        to: ({ orgId }) => `/${orgId}/reports/full-bhuna/deposits`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reports/full-bhuna/deposits`,
      },
      {
        id: 'reports-full-bhuna-marketing',
        label: 'Marketing',
        icon: Megaphone,
        capability: 'reports.full-bhuna',
        to: ({ orgId }) => `/${orgId}/reports/full-bhuna/marketing`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reports/full-bhuna/marketing`,
      },
      {
        id: 'reports-full-bhuna-reviews',
        label: 'Reviews',
        icon: Star,
        capability: 'reports.full-bhuna',
        to: ({ orgId }) => `/${orgId}/reports/full-bhuna/reviews`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reports/full-bhuna/reviews`,
      },
      // The reviews report proper, as opposed to the Full Bhuna section above
      // it. Labelled "Review detail" rather than "Reviews", because a second
      // entry called Reviews two rows apart is a coin toss for the reader.
      // Same pages as Reviews > Reports; a report belongs in both the section
      // that owns the data and the place people go looking for reports.
      {
        id: 'reports-reviews-detail',
        label: 'Review detail',
        icon: Star,
        capability: 'nav.reviews',
        to: ({ orgId }) => `/${orgId}/reviews/reports`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reviews/reports`,
        children: [
          {
            id: 'reports-reviews-overview',
            label: 'Overview',
            icon: Star,
            activeMatch: 'exact',
            capability: 'nav.reviews',
            to: ({ orgId }) => `/${orgId}/reviews/reports`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/reviews/reports`,
          },
          {
            id: 'reports-reviews-metrics',
            label: 'Metrics',
            icon: Star,
            capability: 'nav.reviews',
            to: ({ orgId }) => `/${orgId}/reviews/reports/metrics`,
            venueTo: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/reviews/reports/metrics`,
          },
        ],
      },
      {
        id: 'reports-full-bhuna-wifi',
        label: 'Wi-Fi',
        icon: Wifi,
        capability: 'reports.full-bhuna',
        to: ({ orgId }) => `/${orgId}/reports/full-bhuna/wifi`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reports/full-bhuna/wifi`,
      },
      {
        id: 'reports-full-bhuna-inbox',
        label: 'Inbox',
        icon: Inbox,
        capability: 'reports.full-bhuna',
        to: ({ orgId }) => `/${orgId}/reports/full-bhuna/inbox`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reports/full-bhuna/inbox`,
      },
      {
        id: 'reports-full-bhuna-enquiries',
        label: 'Enquiries',
        icon: MessageSquare,
        capability: 'reports.full-bhuna',
        to: ({ orgId }) => `/${orgId}/reports/full-bhuna/enquiries`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reports/full-bhuna/enquiries`,
      },
      {
        id: 'reports-full-bhuna-loyalty',
        label: 'Loyalty',
        icon: Gift,
        capability: 'reports.full-bhuna',
        to: ({ orgId }) => `/${orgId}/reports/full-bhuna/loyalty`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reports/full-bhuna/loyalty`,
      },
      // ── Always shown, at either scope, Square or not ──
      // Customers moved out of Reports to its own top-level group (Search,
      // Know your customer); /reports/customers still resolves by URL.
      {
        id: 'reports-interactions',
        label: 'Interactions',
        icon: Activity,
        activeMatch: 'prefix',
        // Only a divider when there is something above it to divide from.
        separatorBefore: true,
        to: ({ orgId }) => `/${orgId}/reports/interactions`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reports/interactions`,
      },
    ],
  },
  {
    // All venues → the org-wide brand kits view; a selected venue → that
    // venue's brand kit.
    id: 'brand-kits',
    label: 'Brand Kits',
    icon: Palette,
    capability: 'nav.brand',
    separatorBefore: true,
    to: ({ orgId }) => `/${orgId}/brandkits`,
    venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}/brand`,
  },
  {
    id: 'email-audit',
    capability: 'nav.org-tools',
    label: 'Email Audit',
    icon: ShieldCheck,
    to: ({ orgId }) => `/${orgId}/marketing/email/audit`,
  },
  {
    id: 'hardware',
    capability: 'nav.hardware',
    label: 'Hardware',
    icon: Cpu,
    to: ({ orgId }) => `/${orgId}/hardware`,
  },
  {
    id: 'marketplace',
    capability: 'nav.org-tools',
    label: 'Marketplace',
    icon: ShoppingBag,
    to: ({ orgId }) => `/${orgId}/marketplace/apps`,
    children: [
      {
        id: 'marketplace-apps',
        label: 'Apps',
        icon: ShoppingBag,
        to: ({ orgId }) => `/${orgId}/marketplace/apps`,
      },
      {
        id: 'marketplace-api-keys',
        label: 'API Keys & Webhooks',
        icon: KeyRound,
        to: ({ orgId }) => `/${orgId}/marketplace/api-keys`,
      },
      {
        id: 'marketplace-docs',
        label: 'Documentation',
        icon: ClipboardCheck,
        to: () => 'https://stampede.ai/developer',
        external: true,
      },
    ],
  },
  {
    id: 'scanner',
    label: 'Scanner',
    icon: QrCode,
    capability: 'nav.scanner',
    to: ({ orgId }) => `/${orgId}/scanner`,
  },
  {
    id: 'referrals',
    label: 'Referrals',
    icon: Handshake,
    capability: 'nav.referrals',
    to: ({ orgId }) => `/${orgId}/referrals`,
    badge: { label: 'New', tone: 'new' },
  },
  // Knowledge Base has NO nav row, in either sidebar (2026-09-08). The surface
  // is being reworked around the support agent, and a row pointing at a
  // half-rebuilt page is worse than no row. Both sidebars lose it from here:
  // the old nav is derived from this tree (`old-nav-config.ts`), so there is
  // nothing to remove there. The route and the page are untouched, so anyone
  // working on it still reaches /{orgId}/knowledge-base by URL, and restoring
  // the row is putting an item back here. The top bar's Knowledge Base panel
  // is a separate surface behind its own feature flag and is unaffected.
  {
    /**
     * The permanent home for the release announcement. The login pop-up fires
     * once and is narrowly targeted; this row is the way back to the same news
     * for anyone who dismissed it, or who joined the org afterwards.
     *
     * Deliberately NO `capability`. The pop-up is gated because interrupting
     * someone mid-service has a cost, but a menu row does not, and the pages
     * it leads to are ungated already. Carries an unread count until opened
     * (see `new-sidebar-shell`), which is what makes it findable at all.
     */
    id: 'whats-new',
    // Every role sees this; the capability exists because an ungated section
    // also renders for a subject the app has not resolved yet.
    capability: 'nav.whats-new',
    label: "What's new",
    // The marketing site's own "fresh one" marker — a single brand-gradient
    // dot — rather than a pictogram. See the note on the icon itself.
    icon: NewDotIcon,
    to: ({ orgId }) => `/${orgId}/whats-new`,
    separatorBefore: true,
  },
  {
    // Help articles live publicly on the marketing site, so this row opens
    // them in a new tab. Not a return of the Knowledge Base row (see the note
    // above): that was the agent chat, this is the articles. Shares What's
    // new's capability: both are "learn the product" rows every role sees,
    // gated only so the nav invariant holds.
    id: 'help',
    capability: 'nav.whats-new',
    label: 'Help',
    icon: BookOpen,
    to: () => 'https://stampede.ai/knowledge-base',
    external: true,
  },
  {
    // Internal Stampede staff only — context-free chat with the Engineering
    // Agents (bookings + Wi-Fi). Routes to /:orgId/engineering.
    id: 'engineering-agents',
    label: 'Engineering Agents',
    icon: CraneIcon,
    capability: 'internal.engineering',
    to: ({ orgId }) => `/${orgId}/engineering`,
    separatorBefore: true,
  },
  {
    id: 'chat',
    label: 'Agent Chat',
    icon: MessageSquare,
    /**
     * NOT a pure port: this item was `stampedeOnly`, while the old sidebar
     * gated the same section on `agentMode` — staff PLUS a four-address
     * customer allow-list. The two navs render the same product to the same
     * user depending on a localStorage flag, so disagreeing about who gets
     * agent chat was simply a bug: the allow-listed customers had it on the
     * old UI and lost it on the new one. `nav.chat` resolves to
     * `chat.agent-mode`, which is the old nav's rule, so both now agree.
     */
    capability: 'nav.chat',
    to: ({ orgId }) => `/${orgId}/chat-3`,
  },
]

// Backwards-compat: consumers of the full flat list (unlikely, but safe).
export const orgNav: NavItem[] = [...orgNavPrimary, ...orgNavSecondary]

export const venueNav: NavItem[] = [
  {
    id: 'venue-home',
    label: 'Home',
    icon: Home,
    to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/home2025`,
  },
  {
    id: 'venue-bookings',
    capability: 'nav.bookings',
    label: 'Bookings',
    icon: BookOpen,
    to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/bookings`,
    children: [
      {
        id: 'venue-bookings-day-planner',
        capability: 'bookings.views',
        label: 'Day Planner',
        icon: BookOpen,
        to: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/day-planner`,
      },
      {
        id: 'venue-bookings-list',
        capability: 'bookings.views',
        label: 'List',
        icon: BookOpen,
        to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/bookings/list`,
      },
      {
        id: 'venue-bookings-floor-plans',
        capability: 'bookings.views',
        label: 'Floor Plans',
        icon: BookOpen,
        to: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/floor-plans`,
      },
      {
        id: 'venue-bookings-enquiries',
        capability: 'bookings.enquiries',
        label: 'Enquiries',
        icon: BookOpen,
        to: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/enquiries`,
      },
      {
        id: 'venue-bookings-settings',
        capability: 'bookings.settings',
        label: 'Settings',
        icon: BookOpen,
        to: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/settings`,
      },
    ],
  },
  {
    // Venue-level twin of the org Ordering section: same pages, venue preset.
    id: 'venue-ordering',
    capability: 'nav.ordering',
    label: 'Ordering',
    icon: UtensilsCrossed,
    to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/ordering/orders`,
    isVisible: ({ hasOrdering }) => hasOrdering === true,
    children: [
      {
        id: 'venue-ordering-orders',
        label: 'Orders',
        icon: Receipt,
        to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/ordering/orders`,
      },
      {
        id: 'venue-ordering-menus',
        label: 'Menus',
        icon: Utensils,
        to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/ordering/menus`,
      },
      {
        id: 'venue-ordering-products',
        label: 'Products',
        icon: ShoppingBag,
        to: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/ordering/products`,
      },
      {
        id: 'venue-ordering-modifiers',
        label: 'Modifiers',
        icon: Boxes,
        to: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/ordering/modifiers`,
      },
      {
        id: 'venue-ordering-tables',
        label: 'Tables and QR',
        icon: QrCode,
        to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/ordering/tables`,
      },
    ],
  },
  {
    id: 'venue-wifi',
    label: 'Wi-Fi',
    icon: Wifi,
    to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/wi-fi`,
  },
  {
    id: 'venue-loyalty',
    label: 'Loyalty',
    icon: Heart,
    to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/loyalty`,
  },
  {
    id: 'venue-reviews',
    label: 'Reviews',
    icon: Star,
    to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/reviews`,
    children: [
      {
        id: 'venue-reviews-responses',
        label: 'Responses',
        icon: Star,
        to: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reviews/responses`,
      },
      {
        id: 'venue-reviews-reports',
        label: 'Reports',
        icon: Star,
        // Parent lands on the first child; accordion auto-expands here.
        to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/reviews/reports`,
        children: [
          {
            id: 'venue-reviews-reports-overview',
            label: 'Overview',
            icon: Star,
            // This venue's own report. Exact, or it lights up on Metrics too.
            activeMatch: 'exact',
            to: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/reviews/reports`,
          },
          {
            id: 'venue-reviews-reports-metrics',
            label: 'Metrics',
            icon: Star,
            to: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/reviews/reports/metrics`,
          },
        ],
      },
      {
        id: 'venue-reviews-ai-settings',
        label: 'AI Settings',
        icon: Star,
        // Parent lands on the first child; accordion auto-expands here.
        to: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reviews/ai-settings/auto-generation`,
        children: [
          {
            id: 'venue-reviews-ai-auto-generation',
            label: 'Auto-Generation Rules',
            icon: Sparkles,
            to: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/reviews/ai-settings/auto-generation`,
          },
          {
            id: 'venue-reviews-ai-approval-queue',
            label: 'Approval Queue',
            icon: Inbox,
            to: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/reviews/ai-settings/approval-queue`,
          },
          {
            id: 'venue-reviews-ai-response-prompts',
            label: 'Response Prompts',
            icon: MessageSquare,
            to: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/reviews/ai-settings/response-prompts`,
          },
          {
            id: 'venue-reviews-ai-activity-log',
            label: 'Activity Log',
            icon: FileClock,
            // Staff-only — mirrors the `reviewsActivityLog` feature flag.
            capability: 'internal.staff',
            to: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/reviews/ai-settings/activity-log`,
          },
          {
            id: 'venue-reviews-ai-analytics',
            label: 'Analytics',
            icon: BarChart3,
            to: ({ orgId, serial }) =>
              `/${orgId}/venues/${serial}/reviews/ai-settings/analytics`,
          },
        ],
      },
    ],
  },
  {
    id: 'venue-site',
    label: 'Site',
    icon: Palette,
    to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/site/content`,
    children: [
      {
        id: 'venue-site-content',
        label: 'Content',
        icon: Palette,
        to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/site/content`,
      },
      {
        id: 'venue-site-gallery',
        label: 'Gallery',
        icon: Palette,
        to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/site/gallery`,
      },
    ],
  },
  {
    id: 'venue-brand',
    label: 'Brand',
    icon: Palette,
    to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/brand`,
  },
  {
    id: 'venue-in-the-building',
    label: 'In The Building',
    icon: Users,
    to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/in-the-building`,
  },
  {
    id: 'venue-terminal',
    label: 'Terminal',
    icon: Store,
    to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/terminal`,
  },
  {
    id: 'venue-reports',
    label: 'Reports',
    icon: PieChart,
    to: ({ orgId, serial }) =>
      `/${orgId}/venues/${serial}/reports/monthly-snapshot`,
  },
  {
    id: 'venue-to-do',
    label: 'To-Do',
    icon: MapPin,
    to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/to-do`,
  },
  {
    id: 'venue-settings',
    label: 'Settings',
    icon: Settings,
    to: ({ orgId, serial }) => `/${orgId}/venues/${serial}/settings`,
  },
]

function itemHrefs(item: NavItem, ctx: NavContext): string[] {
  const out: string[] = []
  const org = item.to?.(ctx)
  if (org) out.push(org)
  if (ctx.serial && item.venueTo) {
    out.push(item.venueTo({ ...ctx, serial: ctx.serial }))
  }
  return out
}

/**
 * Walk the nav tree and find the most specific item (including children)
 * whose `to`/`venueTo` URL is the longest prefix of the given pathname.
 * Returns the exact child if one matched, otherwise the top-level section,
 * otherwise null.
 */
export function findItemForPath(
  pathname: string,
  ctx: NavContext
): NavItem | null {
  return findItemChainForPath(pathname, ctx).slice(-1)[0] ?? null
}

/**
 * Like `findItemForPath` but returns the full ancestor chain ending at the
 * best match — e.g. `[square, square-mappings]` for `/venues/:serial/square/mappings`.
 * Used to render breadcrumbs in the top bar.
 */
export function findItemChainForPath(
  pathname: string,
  ctx: NavContext
): NavItem[] {
  const pool: NavItem[] = [...orgNavPrimary, ...orgNavSecondary, ...venueNav]
  interface Match {
    chain: NavItem[]
    len: number
  }
  let best: Match | null = null
  const consider = (item: NavItem, parents: NavItem[]) => {
    const chain = [...parents, item]
    for (const href of itemHrefs(item, ctx)) {
      const matches = pathname === href || pathname.startsWith(`${href}/`)
      if (!matches) continue
      if (!best || href.length > best.len) {
        best = { chain, len: href.length }
      }
    }
    for (const child of item.children ?? []) consider(child, chain)
  }
  for (const section of pool) consider(section, [])

  // `best` is only ever assigned inside `consider`, and TypeScript's control-flow
  // analysis does not follow assignments made in a closure, so it still believes the
  // value is `null` here and narrows the optional chain to `never`. The annotation
  // restores the type the variable actually has at this point.
  // An assertion rather than an annotation: a plain annotation is still subject to the
  // same narrowing and infers `null` too.
  const match = best as Match | null
  return match?.chain ?? []
}

/**
 * Walk every top-level section across the nav tree and find the one whose
 * `to` / `venueTo` URL — or ANY descendant's — is the longest prefix of the
 * given pathname. Returns the top-level section.
 *
 * DESCENDANT, NOT CHILD. This used to look one level down, so a route owned
 * by a grandchild matched nothing at all and the caller was handed `null`
 * as though the URL belonged to no section. The nav is three deep in places
 * (Ordering > Orders > All orders), and that is the same walk the chain
 * already does, so it is the chain's first link.
 */
export function findSectionForPath(
  pathname: string,
  ctx: NavContext
): NavItem | null {
  return findItemChainForPath(pathname, ctx)[0] ?? null
}

/**
 * WHICH SUB-NAV THE URL SHOULD HAVE OPEN, or null for none.
 *
 * The longest matching href wins, not the first: a top-level leaf whose URL
 * sits under another section's prefix (Email Audit at /marketing/email/audit)
 * must beat that section and open nothing, or clicking it drills Marketing.
 *
 * EVERY DESCENDANT IS A CANDIDATE. Looking one level down meant a route
 * owned by a grandchild matched nothing, and the sub-nav closed on arrival:
 * from Pre-ordering, pressing All orders put you back on the top-level menu
 * with the panel you were working in shut. Table ordering and every order's
 * own detail page did the same thing, because none of their URLs sit under
 * the Ordering section's own href.
 */
export function openSectionIdForPath(
  pathname: string,
  sections: readonly NavItem[],
  ctx: NavContext
): string | null {
  let best: { sectionId: string | null; len: number } | null = null
  const consider = (href: string | undefined, sectionId: string | null) => {
    if (!href) return
    if (pathname !== href && !pathname.startsWith(`${href}/`)) return
    if (!best || href.length > best.len) best = { sectionId, len: href.length }
  }
  const walk = (item: NavItem, sectionId: string | null) => {
    consider(resolveHref(item, ctx), sectionId)
    for (const child of item.children ?? []) walk(child, sectionId)
  }
  for (const section of sections) {
    // Inline sections expand in the main list (the accordion derives its own
    // open state from the route), so they never open a drilled panel; nor
    // does a top-level leaf.
    const drillable =
      Boolean(section.children?.length) && !section.inlineChildren
    walk(section, drillable ? section.id : null)
  }
  const match = best as { sectionId: string | null; len: number } | null
  return match?.sectionId ?? null
}
